import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import {
    ActionType,
    CalendarDate,
    marketingGoalActivityFeedLimit,
    MarketingGoalTargets,
    maxMarketingGoalRangeDays,
    maxMarketingGoalRangeYears,
    RegionId,
    TaskTypeId,
    UserId,
    zeroTargets,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { ActivityRowsList } from "components/details-page-v-next/activity-rows";
import { PageHeader, Stage } from "components/layout";
import {
    filterActivityItemsByType,
    toggleActivityTypeFilter,
} from "components/marketing-goals/activity-type-filter";
import {
    defaultGoalDatePreset,
    goalDatePresets,
    GoalDateRange,
    isRangeInPast,
    isSingleWholeMonth,
    isWholeMonthRange,
    monthsInRange,
    priorPeriod,
    rangeDayCount,
    rangeElapsedFraction,
    rangeLabel,
} from "components/marketing-goals/date-range";
import { goalColumnIcon } from "components/marketing-goals/goal-column-icons";
import {
    buildGoalColumns,
    GoalColumn,
    goalColumnBarColor,
    goalColumnKey,
    goalColumnValue,
    hasGoalTargets,
    isActivityGoalColumn,
} from "components/marketing-goals/goal-columns";
import {
    formatMonthRuns,
    monthsWithoutGoals,
    monthsWithoutTeamGoals,
} from "components/marketing-goals/goal-coverage";
import { gapColorClass } from "components/marketing-goals/goal-progress-gap";
import {
    editGoalsPath,
    legacyMarketerParam,
    manageGoalsPath,
    marketerGoalDetailPath,
    marketerGoalRoutes,
    marketerGoalsPath,
} from "components/marketing-goals/goal-routes";
import { GoalFilterTile, GoalSummaryTile } from "components/marketing-goals/goal-tile";
import { currentYearMonth, metricPace } from "components/marketing-goals/pace";
import { RecentActivityRow } from "components/marketing-goals/recent-activity-list";
import { DateRangeControl, DateRangeValue } from "components/reports/date-presets";
import { trpc } from "components/rpc/trpc";
import { ActivityCategoryIcon } from "components/tasks/activity-chip";
import { useActivityTypeOptionsState } from "components/tasks/activity-type-options";
import {
    PrimaryButtonLink,
    SecondaryButtonLink,
    SecondaryCompactButtonLink,
} from "components/ui/button-link";
import { InlineBanner } from "components/ui/inline-banner";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { defaultLinkStyle } from "components/ui/link";
import { MultiUserSelect } from "components/ui/multi-user-select";
import { Spinner } from "components/ui/spinner";
import React, { useMemo, useState } from "react";
import { Redirect, Route, Switch, useLocation, useParams } from "react-router";
import { Link } from "react-router-dom";
import {
    useFeature,
    useIsAllowed,
    useNow,
    useRegionId,
    useUsersForRegion,
} from "store/selectors/hooks";
import { pluralize } from "util/pluralize";
import {
    EditMarketerGoals,
    ManageMarketerGoals,
    NewMarketerGoals,
} from "./manage-marketer-goals";

type RouterOutput = inferRouterOutputs<AppRouter>;
type Attainment = RouterOutput["marketingGoal"]["teamAttainmentForRange"][number];
type RecentActivity = RouterOutput["marketingGoal"]["recentActivityForRange"];

const columnActual = (row: Attainment, column: GoalColumn): number =>
    goalColumnValue(row.actuals, column);

const columnTarget = (row: Attainment, column: GoalColumn): number =>
    row.goal ? goalColumnValue(row.goal, column) : 0;

const MetricCell: React.FC<{ actual: number; target: number; frac: number }> = ({
    actual,
    target,
    frac,
}) => {
    if (!target) {
        return <span className="text-sm text-gray-300">—</span>;
    }
    const pace = metricPace(actual, target, frac);
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`size-1.5 rounded-full ${pace.dot}`} title={pace.label} />
            <span className="text-sm tabular-nums">
                <span className="font-medium text-gray-900">{actual}</span>
                <span className="text-gray-400">/{target}</span>
            </span>
        </span>
    );
};

export const MarketerGoals: React.FC = () => {
    const legacyMarketerId = legacyMarketerParam(useLocation().search);
    if (legacyMarketerId) {
        return <Redirect to={marketerGoalDetailPath(legacyMarketerId)} />;
    }

    return (
        <Switch>
            <Route {...marketerGoalRoutes.report} component={MarketerGoalsReport} />
            <Route {...marketerGoalRoutes.manage} component={ManageMarketerGoals} />
            <Route {...marketerGoalRoutes.newGoals} component={NewMarketerGoals} />
            <Route {...marketerGoalRoutes.editGoals} component={EditMarketerGoals} />
            <Route render={() => <Redirect to={marketerGoalsPath} />} />
        </Switch>
    );
};

const MarketerGoalsReport: React.FC = () => {
    const marketingGoalsEnabled = useFeature("marketingGoals");
    const regionId = useRegionId();
    const effectiveRegionId = regionId ?? ("" as RegionId);
    const canManage = useIsAllowed(ActionType.UpdateRegionSettings);
    const now = useNow();
    const trpcClient = trpc.useTRPC();
    const users = useUsersForRegion(effectiveRegionId);
    const { options: activityTypeOptions, isLoading: optionsLoading } =
        useActivityTypeOptionsState();
    const { marketerUserId: selectedMarketerId } = useParams<{
        marketerUserId?: UserId;
    }>();

    const today = useMemo(() => CalendarDate.fromLocalDate(now), [now]);
    const presets = useMemo(() => goalDatePresets(today), [today]);
    const [range, setRange] = useState<DateRangeValue>(() => {
        const preset = defaultGoalDatePreset(today);
        const initialRange = preset.getRange();
        return {
            startDate: initialRange.start,
            endDate: initialRange.end,
            activePreset: preset.label,
        };
    });
    const [marketerFilter, setMarketerFilter] = useState<UserId[]>([]);

    const goalRange: GoalDateRange = { start: range.startDate, end: range.endDate };
    const rangeOrderValid = rangeDayCount(goalRange) > 0;
    const rangeWithinLimit = rangeDayCount(goalRange) <= maxMarketingGoalRangeDays;
    const rangeValid = rangeOrderValid && rangeWithinLimit;
    const prior = priorPeriod(goalRange);
    const frac = rangeElapsedFraction(goalRange, today);
    const wholeMonths = isWholeMonthRange(goalRange);
    const rangeMonths = useMemo(
        () => monthsInRange({ start: range.startDate, end: range.endDate }),
        [range.startDate, range.endDate],
    );
    const priorLabel = isSingleWholeMonth(goalRange) ? "Prior month" : "Prior period";

    const teamQuery = useQuery({
        ...trpcClient.marketingGoal.teamAttainmentForRange.queryOptions({
            regionId: effectiveRegionId,
            startDate: range.startDate.toString(),
            endDate: range.endDate.toString(),
        }),
        enabled: marketingGoalsEnabled && canManage && !!regionId && rangeValid,
    });
    const priorQuery = useQuery({
        ...trpcClient.marketingGoal.teamAttainmentForRange.queryOptions({
            regionId: effectiveRegionId,
            startDate: prior.start.toString(),
            endDate: prior.end.toString(),
        }),
        enabled:
            marketingGoalsEnabled &&
            canManage &&
            !!regionId &&
            !!selectedMarketerId &&
            rangeValid,
    });
    const recentQuery = useQuery({
        ...trpcClient.marketingGoal.recentActivityForRange.queryOptions({
            regionId: effectiveRegionId,
            marketerUserId: selectedMarketerId ?? ("" as UserId),
            startDate: range.startDate.toString(),
            endDate: range.endDate.toString(),
        }),
        enabled:
            marketingGoalsEnabled &&
            canManage &&
            !!regionId &&
            !!selectedMarketerId &&
            rangeValid,
    });

    const userName = (id: UserId): string => users[id]?.name || "Unknown user";

    const allRows = useMemo(
        () => (teamQuery.data ?? []).filter(hasGoalTargets),
        [teamQuery.data],
    );

    const rows = useMemo(() => {
        if (marketerFilter.length === 0) {
            return allRows;
        }
        return allRows.filter(row => marketerFilter.includes(row.marketerUserId));
    }, [allRows, marketerFilter]);

    const columns = useMemo(
        () => buildGoalColumns(activityTypeOptions, rows),
        [activityTypeOptions, rows],
    );

    const priorActualsFor = (id: UserId): MarketingGoalTargets =>
        (priorQuery.data ?? []).find(row => row.marketerUserId === id)?.actuals ??
        zeroTargets();

    const teamMonthsWithoutGoals = useMemo(
        () => (allRows.length > 0 ? monthsWithoutTeamGoals(rangeMonths, allRows) : []),
        [allRows, rangeMonths],
    );

    const selectedRow: Attainment | undefined = selectedMarketerId
        ? allRows.find(row => row.marketerUserId === selectedMarketerId)
        : undefined;

    if (!marketingGoalsEnabled) {
        return <Redirect to="/reports" />;
    }

    if (selectedMarketerId && teamQuery.isSuccess && !selectedRow) {
        return <Redirect to={marketerGoalsPath} />;
    }

    const title = selectedRow
        ? `Marketer Goal Attainment · ${userName(selectedRow.marketerUserId)}`
        : "Marketer Goal Attainment";

    return (
        <Stage>
            <PageHeader title={title} stackBreakpoint="md">
                <DateRangeControl value={range} onChange={setRange} presets={presets} />
            </PageHeader>
            <div className="min-h-full bg-gray-50 px-4 py-4 lg:px-6">
                {!canManage ? (
                    <div className="py-12 text-center text-sm text-gray-600">
                        You need office administrator access for this office to manage
                        marketer goals.
                    </div>
                ) : !rangeOrderValid ? (
                    <div className="py-12 text-center text-sm text-gray-600">
                        The end date must be on or after the start date.
                    </div>
                ) : !rangeWithinLimit ? (
                    <div className="py-12 text-center text-sm text-gray-600">
                        Choose a date range of {maxMarketingGoalRangeYears} years or
                        fewer.
                    </div>
                ) : teamQuery.isLoading || optionsLoading ? (
                    <div className="py-12 text-center text-gray-500">
                        Loading report...
                    </div>
                ) : teamQuery.isError ? (
                    <div className="py-12 text-center text-red-600">
                        Failed to load report. Please try again.
                    </div>
                ) : (
                    <>
                        {!wholeMonths ? (
                            <div className="mb-4">
                                <InlineBanner type="info">
                                    Goals are monthly. The goals shown are prorated to
                                    your selected dates.
                                </InlineBanner>
                            </div>
                        ) : null}
                        {!selectedRow && teamMonthsWithoutGoals.length > 0 ? (
                            <div className="mb-4">
                                <InlineBanner type="warning">
                                    No goals were set for{" "}
                                    {formatMonthRuns(teamMonthsWithoutGoals)}. Those
                                    months are left out of the goals shown.
                                </InlineBanner>
                            </div>
                        ) : null}
                        {selectedRow ? (
                            <MarketerDetail
                                key={`${selectedRow.marketerUserId}:${range.startDate.toString()}:${range.endDate.toString()}`}
                                row={selectedRow}
                                monthsWithoutGoals={monthsWithoutGoals(
                                    rangeMonths,
                                    selectedRow.goalMonths,
                                )}
                                columns={buildGoalColumns(activityTypeOptions, [
                                    selectedRow,
                                ])}
                                prior={priorActualsFor(selectedRow.marketerUserId)}
                                priorLoading={priorQuery.isLoading}
                                frac={frac}
                                priorLabel={priorLabel}
                                recent={recentQuery.data ?? []}
                                recentLoading={recentQuery.isLoading}
                            />
                        ) : (
                            <>
                                <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 pb-4">
                                    <div className="w-full sm:w-96">
                                        {regionId ? (
                                            <MultiUserSelect
                                                label="Marketer"
                                                placeholder="All marketers"
                                                regionId={regionId}
                                                value={marketerFilter}
                                                onChange={setMarketerFilter}
                                            />
                                        ) : null}
                                    </div>
                                    {rows.length === 0 ? (
                                        <SecondaryButtonLink to={manageGoalsPath}>
                                            Manage goals
                                        </SecondaryButtonLink>
                                    ) : (
                                        <PrimaryButtonLink to={manageGoalsPath}>
                                            Manage goals
                                        </PrimaryButtonLink>
                                    )}
                                </div>

                                {rows.length === 0 ? (
                                    isRangeInPast(goalRange, today) ? (
                                        <div className="py-12 text-center">
                                            <div className="text-base font-semibold text-gray-900">
                                                No goals were set for{" "}
                                                {rangeLabel(goalRange)}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="py-12 text-center">
                                            <div className="text-base font-semibold text-gray-900">
                                                No goals set for {rangeLabel(goalRange)}
                                            </div>
                                            <p className="mt-1.5 text-sm text-gray-600">
                                                Create monthly goals for your marketers to
                                                start tracking attainment.
                                            </p>
                                            <div className="mt-4 flex justify-center">
                                                <PrimaryButtonLink
                                                    to={editGoalsPath(
                                                        currentYearMonth(now),
                                                    )}>
                                                    Set goals
                                                </PrimaryButtonLink>
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    <MarketerLeaderboard
                                        rows={rows}
                                        columns={columns}
                                        frac={frac}
                                        userName={userName}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </Stage>
    );
};

const MarketerLeaderboard: React.FC<{
    rows: Attainment[];
    columns: GoalColumn[];
    frac: number;
    userName: (id: UserId) => string;
}> = ({ rows, columns, frac, userName }) => (
    <div className="overflow-x-auto rounded-lg bg-white ring-1 ring-gray-200">
        <table className="min-w-full">
            <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th scope="col" className="px-4 py-3">
                        Marketer
                    </th>
                    {columns.map(column => (
                        <th
                            key={goalColumnKey(column)}
                            scope="col"
                            className="whitespace-nowrap px-4 py-3 text-right">
                            {column.kind === "activity" ? (
                                <span className="inline-flex items-center gap-1">
                                    <ActivityCategoryIcon
                                        category={column.category}
                                        className="h-3.5 w-3.5 flex-shrink-0 text-gray-400"
                                    />
                                    {column.label}
                                </span>
                            ) : (
                                column.label
                            )}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map(row => (
                    <tr
                        key={row.marketerUserId}
                        className="relative border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                            <Link
                                to={marketerGoalDetailPath(row.marketerUserId)}
                                className="after:absolute after:inset-0 hover:underline">
                                {userName(row.marketerUserId)}
                            </Link>
                        </td>
                        {columns.map(column => (
                            <td
                                key={goalColumnKey(column)}
                                className="whitespace-nowrap px-4 py-3 text-right">
                                <MetricCell
                                    actual={columnActual(row, column)}
                                    target={columnTarget(row, column)}
                                    frac={frac}
                                />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const PriorPeriodFooter: React.FC<{ label: string; actual: number; prior: number }> = ({
    label,
    actual,
    prior,
}) => {
    const delta = actual - prior;
    return (
        <>
            {label}:{" "}
            <span className="font-semibold tabular-nums text-gray-700">{prior}</span>
            <span className={`ml-1 font-semibold ${gapColorClass(actual, prior)}`}>
                ({delta > 0 ? "+" : ""}
                {delta})
            </span>
        </>
    );
};

const initialFeedRows = 10;
const feedRowsIncrement = 25;

const MarketerDetail: React.FC<{
    row: Attainment;
    monthsWithoutGoals: string[];
    columns: GoalColumn[];
    prior: MarketingGoalTargets;
    priorLoading: boolean;
    frac: number;
    priorLabel: string;
    recent: RecentActivity;
    recentLoading: boolean;
}> = ({
    row,
    monthsWithoutGoals: marketerMonthsWithoutGoals,
    columns,
    prior,
    priorLoading,
    frac,
    priorLabel,
    recent,
    recentLoading,
}) => {
    const [selectedType, setSelectedType] = useState<TaskTypeId | null>(null);
    const [visibleCount, setVisibleCount] = useState(initialFeedRows);

    const activityColumns = columns.filter(isActivityGoalColumn);
    const outcomeColumns = columns.filter(column => !isActivityGoalColumn(column));

    const filtered = filterActivityItemsByType(recent, selectedType);
    const visible = filtered.slice(0, visibleCount);
    const hasMore = filtered.length > visibleCount;
    const atFeedCap = recent.length >= marketingGoalActivityFeedLimit;

    const toggleType = (taskTypeId: TaskTypeId) => {
        setSelectedType(current => toggleActivityTypeFilter(current, taskTypeId));
        setVisibleCount(initialFeedRows);
    };
    const clearType = () => {
        setSelectedType(null);
        setVisibleCount(initialFeedRows);
    };

    const tileFooter = (column: GoalColumn): React.ReactNode =>
        priorLoading ? null : (
            <PriorPeriodFooter
                label={priorLabel}
                actual={columnActual(row, column)}
                prior={goalColumnValue(prior, column)}
            />
        );

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between gap-2">
                <SecondaryCompactButtonLink
                    aria-label="Back to all marketers"
                    to={marketerGoalsPath}>
                    <ChevronLeftIcon className="size-4" /> All marketers
                </SecondaryCompactButtonLink>
                <SecondaryButtonLink to={manageGoalsPath}>Edit goals</SecondaryButtonLink>
            </div>
            {marketerMonthsWithoutGoals.length > 0 ? (
                <InlineBanner type="warning">
                    No goals were set for {formatMonthRuns(marketerMonthsWithoutGoals)}.
                    Those months are left out of the goals shown.
                </InlineBanner>
            ) : null}
            {outcomeColumns.length > 0 ? (
                <section>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Outcome goals
                    </div>
                    <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-4">
                        {outcomeColumns.map(column => (
                            <GoalSummaryTile
                                key={goalColumnKey(column)}
                                icon={goalColumnIcon(column)}
                                label={column.label}
                                barColor={goalColumnBarColor(column)}
                                actual={columnActual(row, column)}
                                target={columnTarget(row, column)}
                                frac={frac}
                                footer={tileFooter(column)}
                            />
                        ))}
                    </div>
                </section>
            ) : null}
            <section>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Activity goals
                </div>
                <div className="rounded-lg bg-white ring-1 ring-gray-200">
                    {activityColumns.length > 0 ? (
                        <div className="border-b border-gray-100">
                            <div className="grid grid-cols-2 gap-2 p-3 sm:px-4 lg:grid-cols-3 xl:grid-cols-4">
                                {activityColumns.map(column => (
                                    <GoalFilterTile
                                        key={column.taskTypeId}
                                        icon={goalColumnIcon(column)}
                                        label={column.label}
                                        barColor={goalColumnBarColor(column)}
                                        actual={columnActual(row, column)}
                                        target={columnTarget(row, column)}
                                        frac={frac}
                                        footer={tileFooter(column)}
                                        selected={selectedType === column.taskTypeId}
                                        onToggle={() => toggleType(column.taskTypeId)}
                                    />
                                ))}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 pb-2.5 text-xs text-gray-500 sm:px-4">
                                {selectedType === null ? (
                                    <span>
                                        Showing all activity. Select a type above to
                                        filter.
                                    </span>
                                ) : (
                                    <>
                                        <span>
                                            Showing{" "}
                                            <span className="font-semibold text-gray-700">
                                                {filtered.length}
                                            </span>{" "}
                                            {atFeedCap ? "most recent " : ""}
                                            {pluralize(
                                                filtered.length,
                                                "activity",
                                                "activities",
                                            )}
                                        </span>
                                        <InteractiveTextLink
                                            className="text-xs font-medium"
                                            onClick={clearType}>
                                            Clear filter
                                        </InteractiveTextLink>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : null}
                    {recentLoading ? (
                        <div className="flex justify-center px-4 py-8">
                            <Spinner />
                        </div>
                    ) : recent.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-600">
                            No completed visits or outreach recorded for this period.
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-gray-600">
                            No activity of the selected type in this period.
                        </div>
                    ) : (
                        <ActivityRowsList
                            className="px-3 sm:px-4"
                            testId="goal-recent-activity">
                            {visible.map(item => (
                                <li key={item.id}>
                                    <RecentActivityRow item={item} />
                                </li>
                            ))}
                        </ActivityRowsList>
                    )}
                    {hasMore ? (
                        <div className="border-t border-gray-100 px-3 py-2 sm:px-4">
                            <button
                                type="button"
                                className={`${defaultLinkStyle} text-sm`}
                                onClick={() =>
                                    setVisibleCount(count => count + feedRowsIncrement)
                                }>
                                Show more
                            </button>
                        </div>
                    ) : selectedType === null && atFeedCap ? (
                        <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500 sm:px-4">
                            Showing the {marketingGoalActivityFeedLimit} most recent
                            activities.
                        </div>
                    ) : null}
                </div>
            </section>
        </div>
    );
};
